import { useContext } from 'react'
import { ChatbotUIChatContext } from '@/context/chat'
import { ChatbotUIContext } from '@/context/context'
import { getAssistantCollectionsByAssistantId } from '@/db/assistant-collections'
import { getAssistantFilesByAssistantId } from '@/db/assistant-files'
import { getAssistantToolsByAssistantId } from '@/db/assistant-tools'
import { getCollectionFilesByCollectionId } from '@/db/collection-files'
import { Tables } from '@/supabase/types'
import { LLMID } from '@/types'
import { toast } from 'sonner'
import {
  useScopedAttachmentKey,
  useScopedSetUserInput
} from './use-scoped-user-input'

interface UsePromptAndCommandOptions {
  chatId?: string
}

type AssistantSelectionOptions = {
  preserveInput?: boolean
}

export const usePromptAndCommand = ({
  chatId
}: UsePromptAndCommandOptions = {}) => {
  const {
    activeAttachmentKey,
    getAttachmentStoreKey,
    getScopedAttachmentBindings,
    setIsPromptPickerOpen,
    setIsFilePickerOpen,
    setSlashCommand,
    setHashtagCommand,
    setUseRetrieval,
    setToolCommand,
    setIsToolPickerOpen,
    setAtCommand,
    setIsAssistantPickerOpen,
    setSelectedAssistant,
    setPromptVariables,
    setShowPromptVariables,
    profile
  } = useContext(ChatbotUIContext)

  const { selectedTools, setSelectedTools, setChatSettings } = useContext(ChatbotUIChatContext)
  // Setter-only — never subscribe to the draft string here. ChatUI / message
  // lists call this hook; reading the value would re-render them every keystroke.
  const setUserInput = useScopedSetUserInput()
  const attachmentKey = useScopedAttachmentKey({
    activeAttachmentKey,
    chatId,
    getAttachmentStoreKey
  })
  const attachmentBindings = getScopedAttachmentBindings(attachmentKey)
  const attachmentState = attachmentBindings.getSnapshot()
  const chatFiles = attachmentState.chatFiles
  const newMessageFiles = attachmentState.newMessageFiles

  const handleInputChange = (
    value: string,
    options?: {
      enableAssistantCommand?: boolean
    }
  ) => {
    const enableAssistantCommand = options?.enableAssistantCommand ?? true
    const assistantTextRegex = new RegExp(`^${profile?.assistant_command}([^ ]*)$`)
    const promptTextRegex = new RegExp(`^${profile?.prompt_command}([^ ]*)$`)
    const filesTextRegex = new RegExp(`^${profile?.files_command}([^ ]*)$`)
    const toolTextRegex = new RegExp(`^${profile?.tools_command}([^ ]*)$`)
    const assistantMatch = value.match(assistantTextRegex)
    const promptMatch = value.match(promptTextRegex)
    const filesMatch = value.match(filesTextRegex)
    const toolMatch = value.match(toolTextRegex)

    if (enableAssistantCommand && assistantMatch) {
      setIsAssistantPickerOpen(true)
      setAtCommand(assistantMatch[1])
    } else if (promptMatch) {
      setIsPromptPickerOpen(true)
      setSlashCommand(promptMatch[1])
    } else if (filesMatch) {
      setIsFilePickerOpen(true)
      setHashtagCommand(filesMatch[1])
    } else if (toolMatch) {
      setIsToolPickerOpen(true)
      setToolCommand(toolMatch[1])
    } else {
      setIsPromptPickerOpen(false)
      setIsFilePickerOpen(false)
      setIsToolPickerOpen(false)
      setIsAssistantPickerOpen(false)
      setSlashCommand('')
      setHashtagCommand('')
      setToolCommand('')
      setAtCommand('')
    }

    setUserInput(value)
  }

  const handleSelectPromptWithVariables = (prompt: Tables<'prompts'>) => {
    const regex = /\{\{.*?\}\}/g
    const matches = prompt.content.match(regex)

    if (matches) {
      const newPromptVariables = matches.map((match) => ({
        promptId: prompt.id,
        name: match.replace(/\{\{|\}\}/g, ''),
        value: ''
      }))

      setPromptVariables(newPromptVariables)
      setShowPromptVariables(true)
    } else {
      handleSelectPrompt(prompt)
      setIsPromptPickerOpen(false)
    }
  }

  const handleSelectPrompt = (prompt: Tables<'prompts'>) => {
    setIsPromptPickerOpen(false)
    setUserInput(prev => prev.replace(/\/[^ ]*$/, '') + prompt.content)
  }

  const handleSelectHistoryMessage = (message: string) => {
    setIsPromptPickerOpen(false)
    setUserInput(prev => prev.replace(/\/[^ ]*$/, '') + message)
  }

  const handleSelectUserFile = async (file: Tables<'files'>) => {
    attachmentBindings.setShowFilesDisplay(true)
    setIsFilePickerOpen(false)
    setUseRetrieval(true)

    if (newMessageFiles.some((chatFile) => chatFile.id === file.id)) {
      attachmentBindings.setNewMessageFiles((prev) => {
        const result = prev.filter((prevFile) => prevFile.id !== file.id)

        if (result.length === 0 && chatFiles.length === 0) {
          setUseRetrieval(false)
        }

        return result
      })

      attachmentBindings.setChatFiles((prev) => {
        const result = prev.filter((chatFile) => chatFile.id !== file.id)

        if (result.length === 0 && newMessageFiles.length === 0) {
          setUseRetrieval(false)
        }

        return result
      })

      return
    }

    attachmentBindings.setNewMessageFiles((prev) => {
      const fileAlreadySelected =
        prev.some((prevFile) => prevFile.id === file.id) ||
        chatFiles.some((chatFile) => chatFile.id === file.id)

      if (!fileAlreadySelected) {
        return [
          ...prev,
          {
            id: file.id,
            name: file.name,
            type: file.type,
            file: null
          }
        ]
      }
      return prev
    })

    setUserInput(prev => prev.replace(/#[^ ]*$/, ''))
  }

  const handleSelectUserCollection = async (collection: Tables<'collections'>) => {
    attachmentBindings.setShowFilesDisplay(true)
    setIsFilePickerOpen(false)
    setUseRetrieval(true)

    const collectionFiles = await getCollectionFilesByCollectionId(collection.id)

    attachmentBindings.setNewMessageFiles((prev) => {
      const newFiles = collectionFiles.files
        .filter(
          (file) =>
            !prev.some((prevFile) => prevFile.id === file.id) &&
            !chatFiles.some((chatFile) => chatFile.id === file.id)
        )
        .map((file) => ({
          id: file.id,
          name: file.name,
          type: file.type,
          file: null
        }))

      return [...prev, ...newFiles]
    })

    setUserInput(prev => prev.replace(/#[^ ]*$/, ''))
  }

  const handleSelectTool = (tool: Tables<'tools'>) => {
    setIsToolPickerOpen(false)
    setUserInput(prev => prev.replace(/![^ ]*$/, ''))
    if (selectedTools.includes(tool)) {
      setSelectedTools(selectedTools.filter((t) => t !== tool))
      return
    }
    setSelectedTools((prev) => [...new Set([...prev, tool])])
  }

  const handleSelectAssistant = async (
    assistant: Tables<'assistants'>,
    options: AssistantSelectionOptions = {}
  ) => {
    try {
      setIsAssistantPickerOpen(false)
      if (!options.preserveInput) {
        setUserInput(prev => prev.replace(/@[^ ]*$/, ''))
      }
      setSelectedAssistant(assistant)

      setChatSettings((prevSettings) => ({
        ...prevSettings,
        model: assistant.model as LLMID,
        prompt: assistant.prompt,
        temperature: assistant.temperature,
        contextLength: assistant.context_length,
        includeProfileContext: assistant.include_profile_context,
        includeWorkspaceInstructions: assistant.include_workspace_instructions,
        embeddingsProvider: assistant.embeddings_provider as 'openai' | 'local',
        reflectMode: prevSettings?.reflectMode
      }))

      let allFiles = []

      const assistantFiles = (await getAssistantFilesByAssistantId(assistant.id)).files
      allFiles = [...assistantFiles]
      const assistantCollections = (await getAssistantCollectionsByAssistantId(assistant.id))
        .collections
      for (const collection of assistantCollections) {
        const collectionFiles = (await getCollectionFilesByCollectionId(collection.id)).files
        allFiles = [...allFiles, ...collectionFiles]
      }
      const assistantTools = (await getAssistantToolsByAssistantId(assistant.id)).tools

      setSelectedTools(assistantTools as unknown as Tables<'tools'>[])
      attachmentBindings.setChatFiles(
        allFiles.map((file) => ({
          id: file.id,
          name: file.name,
          type: file.type,
          file: null
        }))
      )
      if (allFiles.length > 0) {
        attachmentBindings.setShowFilesDisplay(true)
      }
    } catch (error) {
      console.error(error)
      toast.error("Couldn't finish that. Try again.")
    }
  }

  return {
    handleInputChange,
    handleSelectPrompt,
    handleSelectUserFile,
    handleSelectUserCollection,
    handleSelectTool,
    handleSelectAssistant,
    handleSelectPromptWithVariables,
    handleSelectHistoryMessage
  }
}
